import { useEffect, useState } from 'react';
import { getFileBlobUrl } from '@/api/query/file';
import { normalizeFileId } from '@/common/hooks/useImage';

const videoCache = new Map();
const pendingLoads = new Map();

const loadVideo = async (normalizedFileId) => {
	if (videoCache.has(normalizedFileId)) {
		return videoCache.get(normalizedFileId);
	}

	if (pendingLoads.has(normalizedFileId)) {
		return pendingLoads.get(normalizedFileId);
	}

	const request = getFileBlobUrl(normalizedFileId, 'video')
		.then((result) => {
			if (result?.url) {
				videoCache.set(normalizedFileId, result);
			}

			return result;
		})
		.catch(() => null)
		.finally(() => {
			pendingLoads.delete(normalizedFileId);
		});

	pendingLoads.set(normalizedFileId, request);

	return request;
};

const useVideoLoader = (fileId) => {
	const normalizedFileId = normalizeFileId(fileId);
	const cached = normalizedFileId ? videoCache.get(normalizedFileId) : null;
	const [videoSrc, setVideoSrc] = useState(cached?.url ?? null);
	const [contentType, setContentType] = useState(cached?.contentType ?? null);
	const [isLoading, setIsLoading] = useState(Boolean(normalizedFileId && !cached));
	const [hasError, setHasError] = useState(false);

	useEffect(() => {
		if (!normalizedFileId) {
			setVideoSrc(null);
			setContentType(null);
			setIsLoading(false);
			setHasError(false);
			return;
		}

		const cachedFile = videoCache.get(normalizedFileId);

		if (cachedFile) {
			setVideoSrc(cachedFile.url);
			setContentType(cachedFile.contentType);
			setIsLoading(false);
			setHasError(false);
			return;
		}

		let isActive = true;
		setIsLoading(true);
		setVideoSrc(null);
		setContentType(null);
		setHasError(false);

		loadVideo(normalizedFileId).then((result) => {
			if (!isActive) {
				return;
			}

			setVideoSrc(result?.url ?? null);
			setContentType(result?.contentType ?? null);
			setIsLoading(false);
			setHasError(Boolean(!result?.url));
		});

		return () => {
			isActive = false;
		};
	}, [normalizedFileId]);

	return { videoSrc, contentType, isLoading, hasError };
};

export default useVideoLoader;
